(ns nosco.ui.modals3
  (:require
   ["@react-spring/web" :as spring]
   ["react" :as React]
   ["react-useportal$default" :as usePortal]
   [goog.dom.classlist]
   [goog.style]
   [hx.hooks :as hooks]
   [hx.react :as hx :refer [defnc]]
   [nosco.hooks]
   [nosco.icons2 :as icons2]
   [nosco.react :as nosco-react]
   [nosco.ui.base]
   [nosco.ui.buttons2 :as buttons2]
   [nosco.ui.layouts :as layouts]
   [nosco.ui.loading :as loading]
   [nosco.ui.popup :as popup]
   [nosco.ui.text :as text]
   [nosco.util :as util]))

(defonce ^:private scrollbar-width (delay (goog.style/getScrollbarWidth)))
(defonce ^:private padding-right (delay (.-document.body.style.paddingRight js/window)))

(defn- set-padding-right [^js el v]
  (set! (.-style.paddingRight el) v))

;; Nested modals may call use-lock-body-scroll multiple times,
;; make sure we restore scroll when the last modal is off the screen
(defonce lock-body-scroll-counter (atom 0))

(defn use-lock-body-scroll []
  (hx.hooks/useLayoutEffect
   (fn []
     (let [counter (swap! lock-body-scroll-counter inc)
           header (js/document.querySelector "#nosco-topnav-header")
           body js/document.body
           old-padding-right @padding-right
           padding-right (str @scrollbar-width "px")]
       (when (= counter 1)
         (set-padding-right body padding-right)
         (when header
           (set-padding-right header padding-right))
         (goog.dom.classlist/add js/document.body "modal-open"))
       (fn []
         (let [counter (swap! lock-body-scroll-counter dec)]
           (when (zero? counter)
             (set-padding-right body old-padding-right)
             (when header
               (set-padding-right header old-padding-right))
             (goog.dom.classlist/remove js/document.body "modal-open"))))))
   []))

;; this is a new implementation
(defnc LockBodyScroll []
  (use-lock-body-scroll)
  ;; this class seems to not be present in any CSS, perhaps it's for debugging?
  [:div {:class "lock-body-scroll"
         :style {:display "none"}}])

;; This is the original implementation which is conditional,
;; it seems to be used only on mobile so I put a suffix in the name
(defnc LockBodyScrollMobile [{:keys [lock]}]
  (when lock
    [:<>
     [LockBodyScroll]
     [:div {:style {:display "none"}}]]))

(defnc NavigationButton
  "Always render the container of a fixed width/height for layout
  purposes, but hide the action button if necessary."
  [{:keys [on-click dir label show]}]
  [:div {:style {:margin-left (when (= dir :right) "1.5rem")
                 :margin-right (when (= dir :left) "1.5rem")
                 :width "3rem"
                 :pointer-events (if show "auto" "none")}}
   (when show
     [buttons2/Button {:variant :icon-light
                       :size :large
                       :aria-label (if (= dir :left) "Previous" "Next")
                       :icon (if (= dir :left)
                               icons2/ChevronLeft-large
                               icons2/ChevronRight-large)
                       :on-click (fn [e]
                                   (.preventDefault e)
                                   (.stopPropagation e)
                                   (on-click))}])])

(defn use-navigation [{:keys [on-prev on-next]}]
  (nosco.hooks/maybe-use-event-listener
   (or on-prev on-next)
   js/window
   "keydown"
   (fn [event]
     (when-not
      ;; ignore events that are happening inside form elements
      ;; and such
      (util/handles-left-right-arrows? (.-target event))
       (case (.-key event)
         "ArrowLeft"
         (when on-prev (on-prev))
         "ArrowRight"
         (when on-next (on-next))
         nil)))))

(defnc ModalHeader [{:keys [title on-close]}]
  [:header {:class "nosco-modal-3-header"}
   [text/Text {:variant text/Heading-Md}
    title]
   (when on-close
     [buttons2/Button {:variant :icon-light
                       :icon icons2/Close-large
                       :on-click on-close
                       :class "nosco-modal-3-header__close-btn"}])])

(defnc ModalBody [{:keys [children] :as props}]
  [:div (nosco-react/merge-props {:class "nosco-modal-3-body"}
                                 props)
   children])

(defnc ModalFooter [{:keys [children justify]
                     :or {justify :end}
                     :as props}]
  [:footer {:class "nosco-modal-3-footer"}
   [layouts/Row (nosco-react/merge-props {:gap "8px"
                                          :class (case justify
                                                   :end "justify-content-end"
                                                   :start "justify-content-start"
                                                   :between "justify-content-between"
                                                   nil nil)}
                                         (dissoc props :justify))
    children]])

(defnc Modal [{:keys [children
                      class
                      style
                      variant
                      height-variant
                      on-prev
                      on-next]} ref]
  {:pre [(#{:idea :x-wide :wide :normal :narrow} variant)
         (or (nil? height-variant)
             (#{:full} height-variant))]}

  (let [has-nav (= variant :idea)]
    (when has-nav
      (use-navigation {:on-prev on-prev
                       :on-next on-next}))
    [:<>
     (when has-nav
       [NavigationButton {:dir :left
                          :show (some? on-prev)
                          :on-click on-prev}])
     [:section {:class [(if (not= variant :idea)
                          "nosco-modal-3"
                          "nosco-modal-3--base")
                        class
                        (when (= height-variant :full)
                          "nosco-modal-3--full-height")
                        (when (= variant :idea)
                          "nosco-modal-3--idea")
                        (when (= variant :narrow)
                          "nosco-modal-3--narrow")
                        (when (= variant :normal)
                          "nosco-modal-3--normal")
                        (when (= variant :wide)
                          "nosco-modal-3--wide")
                        (when (= variant :x-wide)
                          "nosco-modal-3--x-wide")]
                :ref ref
                :style style
                :on-click #(.stopPropagation %)}
      children]
     (when has-nav
       [NavigationButton {:dir :right
                          :show (some? on-next)
                          :on-click on-next}])]))
(nosco.react/forward-ref! Modal)

(defnc FullScreenModal [{:keys [children variant] :as props} ref]
  {:wrap [(React/forwardRef)]}
  [:div (nosco-react/merge-props
         {:ref ref
          :class ["nosco-modal-full-screen"
                  (when (= variant :inset)
                    "nosco-modal-full-screen--inset")]}
         (dissoc props :children))
   children])

(defnc ModalBackdrop [{:keys [children
                              on-close
                              showing
                              inner-class]}]
  (let [ref (React/useRef)
        inner-ref (React/useRef)]
    (hx.hooks/useEffect
     ;; TODO with a bit more effort and by listening to `transitionend` event, we can reverse the
     ;; animation with pure CSS - but we need to carefully coordinate between the parent
     ;; backdrop and the inner backdrop, and probably cascade the transtions or deal with the
     ;; visibility: hidden aspect of things
     (fn []
       (if showing
         (some-> (.-current ref) .-classList (.add "nosco-modal-3-backdrop--visible"))
         (some-> (.-current ref) .-classList (.remove "nosco-modal-3-backdrop--visible"))))
     [showing])
    ;; TODO handle escape key close
    [nosco.ui.base/ModalPortal
     [:<>
      (when showing [LockBodyScroll])
      [:div {:ref ref
             :on-mouse-down
             (fn [^js event]
               (when on-close
                 (when (or (= (.-current inner-ref) (.-target event))
                           (= (.-current ref) (.-target event)))
                   (on-close))))
             :class "nosco-modal-3-backdrop"}
       [:div {:ref inner-ref :class (or inner-class "nosco-modal-3-backdrop__inner")}
        [:provider {:context popup/OverlayContainerContext
                    :value (.-current inner-ref)}
         (when showing
           children)]]]]]))

(defnc MobileModal
  "A modal designed for mobile screens.

  Props:

  - :full-height - Expands the modal to the full screen height
  - :flex-contents - Makes the inner modal a flex container, useful to keep the header sticky
  - :open - Whether the modal is open
  - :on-close - Called when the modal should close"
  [{:keys [full-height
           flex-contents
           open
           on-close
           on-transition-destroyed
           children]}
   outer-ref]
  {:wrap (React/forwardRef)}
  (let [inner-ref (React/useRef)
        Portal (.-Portal (usePortal #js {:bindTo (hooks/useContext popup/OverlayContainerContext)}))
        transition (spring/useTransition open
                                         #js {:config #js {:duration 250 :easing (.-easeInOutQuart spring/easings)}
                                              :from #js {:translateY "100%"}
                                              :enter #js {:translateY "0%"}
                                              :leave #js {:translateY "100%"}
                                              :onDestroyed on-transition-destroyed})
        backdrop-transition (spring/useTransition open
                                                  #js {:config #js {:duration 250 :easing (.-easeInOutQuart spring/easings)}
                                                       :from #js {:opacity 0}
                                                       :enter #js {:opacity 0.7}
                                                       :leave #js {:opacity 0}})
        inner-ref (React/useRef)]
    (hooks/useLayoutEffect
     (fn []
       (when open
         ;; Prevent the iOS Safari address bar from obscuring the modal contents
         (let [set-height! (fn []
                             (when (.-current inner-ref)
                               (set! (.. inner-ref -current -style -height)
                                     (str (.-innerHeight js/window) "px"))))]
           (set-height!)
           (.addEventListener js/window "resize" set-height!)
           (fn []
             (.removeEventListener js/window "resize" set-height!))))
       js/undefined)
     [open])
    [Portal
     [LockBodyScrollMobile {:lock open}]
     [:div {:ref outer-ref}
      (backdrop-transition
       (fn [style open*]
         (when open*
           (hx/f
            [(.-div spring/animated)
             {:className "nosco-mobile-modal-backdrop"
              :style style
              :onClick on-close}]))))
      (transition
       (fn [style open*]
         (when open*
           (hx/f
            [(.-div spring/animated)
             {:ref inner-ref
              :className (cond-> "nosco-mobile-modal"
                           flex-contents (str " nosco-mobile-modal--flex")
                           full-height (str " nosco-mobile-modal--full-height"))
              :style style}
             [:div {:ref inner-ref
                    :class ["nosco-mobile-modal__inner"]}
              [:provider {:context popup/OverlayContainerContext
                          :value (.-current inner-ref)}
               children]]]))))]]))

(defnc MobileModalHeader [{:keys [on-close sticky children]}]
  [:div {:class ["nosco-mobile-modal-header"
                 (when sticky "nosco-mobile-modal-header--sticky")]}
   children
   (when on-close
     [:div {:class ["nosco-mobile-modal-header__close"]}
      [buttons2/Button
       {:variant :icon-light
        :icon icons2/Close-large
        :on-click on-close}]])])

(defnc MobileModalFooter [{:keys [children]}]
  [:div {:class ["nosco-mobile-modal-footer"]}
   children])

(defnc MobileModalHeaderTitle [{:keys [title children]}]
  [:div {:class "nosco-mobile-modal-header-title"}
   [text/Text
    {:el :span
     :variant text/Heading-Md}
    title]
   children])

(defnc ModalDialog [{:keys [showing on-close title children
                            cancel-label
                            confirm-label
                            confirm-props
                            on-confirm
                            loading
                            size
                            variant]
                     :or {size :normal
                          variant :default}
                     :as props}]
  {:pre [(#{:normal :narrow :wide :x-wide} size)
         (#{:destructive :default} variant)]}
  [ModalBackdrop {:showing showing
                  :on-close on-close}
   [Modal (nosco.react/merge-props
           {:variant size}
           (select-keys props [:class]))
    (when title
      [ModalHeader {:title title
                    :on-close on-close}])
    [ModalBody
     [React/Suspense {:fallback (hx/f [loading/LoadingNew])}
      children]]
    (when (or cancel-label on-confirm)
      [ModalFooter
       (when cancel-label
         [buttons2/Button {:label cancel-label
                           :variant buttons2/NewSubtle
                           :on-click on-close}])
       (when on-confirm
         [buttons2/Button (nosco.react/merge-props
                           {:variant buttons2/NewPrimary
                            :tone (if (= variant :destructive) buttons2/ToneError buttons2/ToneBrand)
                            :icon (when (= variant :destructive) icons2/Delete-small)
                            :loading loading
                            :on-click on-confirm
                            :label confirm-label}
                           confirm-props)])])]])

(defn use-prevent-escape []
  (let [listener (fn [e]
                   (when (= (.-key e) "Escape")
                     (.stopPropagation e)
                     (.preventDefault e)))]

    (hooks/useEffect (fn []
                       (.addEventListener js/document "keydown" listener)
                       #(.removeEventListener js/document "keydown" listener))
                     [])))

(defnc PreventEscape []
  (use-prevent-escape))

(defnc NativeHTMLDialog [{:keys [visible? close children
                                 prevent-escape?] :as props}]
  (let [ref (React/useRef)]
    (hooks/useEffect
     (fn []
       (if visible?
         (when-not (.-open (.-current ref))
           (.showModal (.-current ref)))
         (.close (.-current ref))))
     [visible?])
    (hooks/useLayoutEffect
     (fn []
       (let [on-close (fn [_] (when close (close)))]
         (.addEventListener (.-current ref) "close" on-close)
         #(.removeEventListener (.-current ref) "close" on-close))))
    [:<>
     (when (and visible? prevent-escape?)
       [PreventEscape])
     (when visible?
       [LockBodyScroll])
     [:dialog (nosco-react/merge-props {:ref ref
                                        :class "nosco-dialog-modal"}
                                       (dissoc props
                                               :visible?
                                               :prevent-escape?
                                               :close))
      children]]))
